import { ContentWrapper, DataTable } from "@components/index";
import { Button, Col, Input, Pagination, Select, Space, Spin, TableColumnsType, Tabs, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { get } from "@services/api.service";
import { useNotification } from "@hooks/useNotification";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useHasAccess } from "@hooks/useHasAccess";
import { getColoriezedValue, getColorizedValueDocuSign } from "~/Utils/getColorizedValue";
import { useQuery } from "~/Hooks/useQuery";
import { getFormattedDate, getUrlwithParams } from "@utils/CommonUtils";
import { debounce } from "lodash";

export const LogManagement = () => {
  const [data, setData] = useState<any>([]);
  const [apiUrl, setApiUrl] = useState<any>("");
  const { hasPermissions, hasRole } = useHasAccess();
  const { openToast } = useNotification();
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setpage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageStart, setPageStart] = useState<number>(0);
  const [searchString, setSearchString] = useState("");
  const [clearSearch, setClearSearch] = useState(false);

  const query = useQuery();
  const navigate = useNavigate();
  const [orderByName, setOrderByName] = useState("actionPerformedOn");
  const [activeTab, setActiveTab] = useState(
    query?.get("activeTab") !== null && query?.get("activeTab") !== undefined ? query?.get("activeTab") : ""
  );

  useEffect(() => {
    if (hasRole(["LOA_REVIEWER"])) {
      if (!query.get("activeTab")) {
        navigate(`/logs?activeTab=template`, {
          replace: true,
        });
      }
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setClearSearch(false);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getRecords(order, column.dataIndex, searchString, pageStart);
  };
  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    setIsLoading(true);
    let url = `/v1/auditlog/get-all-logs?page=${currentPage}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${encodeURIComponent(searchString)}&module=${activeTab}`;
    setApiUrl(url);

    get(url)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          setTotalRecords(res.response.data.totalCount);
          let envelopes = res.response.data.records;
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        openToast({
          content: "Getting Contracts failed",
          type: "error",
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, pageStart);
  }, [pageStart]);

  useEffect(() => {
    setPageStart(0);
    getRecords(sortOrder, orderByName, searchString, searchString.length > 0 ? 0 : pageStart);
  }, [searchString, query, activeTab]);

  const columns: TableColumnsType = [
    {
      title: "Action",
      dataIndex: "action",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.action || "").localeCompare(b.action || ""),
      sortOrder:
        orderByName === "action" && sortOrder === "asc"
          ? "ascend"
          : orderByName === "action" && sortOrder === "desc"
            ? "descend"
            : null,
    },
    {
      title: "Details",
      dataIndex: "detail",
      render: (value: any, record: any) => {
        if (!value) return <Typography.Text disabled>-</Typography.Text>;

        let decoded = value;
        try {
          decoded = decodeURIComponent(value);
        } catch (e) {
          decoded = value;
        }

        const formatted = decoded.replace(/\b(\d+)\.(\d+)\b/g, (_: string, major: string, minor: string) => {
          const version = (Number(major) - 1) * 10 + Number(minor);
          return `V${version}`;
        });

        return formatted;
      },
      // sorter: (a: any, b: any) => (a?.sender?.commentedBy || "").localeCompare(b?.sender?.commentedBy || ""),
    },
    {
      title: "Performed By",
      dataIndex: "actionPerformedBy",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.actionPerformedBy || "").localeCompare(b.actionPerformedBy || ""),
      sortOrder:
        orderByName === "actionPerformedBy" && sortOrder === "asc"
          ? "ascend"
          : orderByName === "actionPerformedBy" && sortOrder === "desc"
            ? "descend"
            : null,
    },
    {
      title: "Performed At",
      dataIndex: "actionPerformedOn",
      render: (value: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      // change here
      sorter: (a: any, b: any) => (a.actionPerformedOn || "").localeCompare(b.actionPerformedOn || ""),
      sortOrder:
        orderByName === "actionPerformedOn" && sortOrder === "asc"
          ? "ascend"
          : orderByName === "actionPerformedOn" && sortOrder === "desc"
            ? "descend"
            : null,
    },
  ];

  const documentColumns: TableColumnsType = [
    {
      title: "Project ID",
      dataIndex: "projectId",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.projectId || "").localeCompare(b.projectId || ""),
      sortOrder:
        orderByName === "projectId" && sortOrder === "asc"
          ? "ascend"
          : orderByName === "projectId" && sortOrder === "desc"
            ? "descend"
            : null,
    },
    {
      title: "Project Title",
      dataIndex: "projectTitle",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.projectTitle || "").localeCompare(b.projectTitle || ""),
      sortOrder:
        orderByName === "projectTitle" && sortOrder === "asc"
          ? "ascend"
          : orderByName === "projectTitle" && sortOrder === "desc"
            ? "descend"
            : null,
    },
    ...columns,
  ];

  const logTable = (key: string) => {
    let cols = key === "document" ? documentColumns : columns;
    return (
      <>
        <DataTable
          columns={cols?.map((column) => ({
            ...column,
            onHeaderCell: (column: any) => ({
              onClick: () => {
                if (column.sorter) {
                  handleTableSort(column, sortOrder, page ? page : 0);
                }
              },
            }),
          }))}
          tabOffset={60}
          multiSelect={false}
          moduleName={"Logs"}
          className="table-clickable"
          tableData={
            data
              ? data?.map((d: any, index: number) => {
                  return {
                    ...d,
                    key: index,
                  };
                })
              : null
          }
          isLoading={false}
          exportFileName="SPA"
          hideExport={true}
          onClick={(record) => {}}
          totalRecords={totalRecords}
          inputDisable={isLoading}
          isPagination={true}
          isServerSearchApi={true}
          clearStringSearch={clearSearch}
          ctaColSpan={19}
          onSearchChange={handleSearchChange}
          searchPlaceholderText={key === "document" ? "Search Project ID / Action / Detail" : "Search Action / Detail"}
          // exportApiUrl={apiUrl}
          recordsPerPage={recordsPerPage}
        />
        <div className="pagination">
          <Pagination
            defaultCurrent={1}
            current={page + 1}
            total={totalRecords}
            onChange={(page) => {
              const pageSize = 10;
              const startIndex = page - 1;
              setpage(page - 1);
              setPageStart(startIndex);
              getRecords(sortOrder, orderByName, searchString, startIndex);
            }}
            showSizeChanger={false}
          />
        </div>
      </>
    );
  };

  const items = [
    {
      label: "Templates",
      key: "template",
      children: logTable("template"),
    },
    {
      label: "SPA",
      key: "document",
      children: logTable("document"),
    },
    {
      label: "Subsidiary/Division",
      key: "subsidiary",
      children: logTable("subsidiary"),
    },
    {
      label: "Cost Centre",
      key: "division",
      children: logTable("division"),
    },
    {
      label: "Approvers",
      key: "approver",
      children: logTable("approver"),
    },
    {
      label: "Watchers",
      key: "watcher",
      children: logTable("watcher"),
    },
    {
      label: "Sectratariat, PCD",
      key: "oneStopCentre",
      children: logTable("oneStopCentre"),
    },
    {
      label: "User Management",
      key: "user",
      children: logTable("user"),
    },
  ];

  return (
    <ContentWrapper title={"SPA Log Management"}>
      {isLoading && (
        <div className="loadingOverlay">
          <Space size="middle">
            <Spin size="large" />
          </Space>
        </div>
      )}
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          setPageStart(0);
          setpage(0);
          //   setStatus("");
          setActiveTab(activeKey);
          navigate(`/logs?activeTab=${activeKey}`, { replace: true });
          setSearchString("");
          setClearSearch(true);
        }}
      ></Tabs>
    </ContentWrapper>
  );
};
